<template>
  <div class="video-post-page" data-layout="linjie-video-post">
    <header class="video-post-banner">
      <download-banner @onInvokeApp="onInvokeApp" />
    </header>

    <main class="video-post-media">
      <div v-if="props.loading" class="video-post-state">帖子加载中...</div>
      <div v-else-if="displayError" class="video-post-state">
        {{ displayError }}
      </div>
      <div
        v-else-if="isReviewing"
        class="video-post-state video-post-state--reviewing"
      >
        <DuIcon name="time-circle-filled" size='5.333333333333334rem' />
        <span>帖子审核中</span>
      </div>

      <template v-else>
        <video
          ref="videoRef"
          class="video-post-media__video"
          :poster="postData.cover"
          webkit-playsinline
          playsinline
          preload="metadata"
          @play="isPlaying = true"
          @pause="isPlaying = false"
          @ended="isPlaying = false"
        >
          <source :src="videoUrl" type="video/mp4" />
        </video>
        <button
          type="button"
          class="video-post-media__play-mask"
          aria-label="播放或暂停视频"
          @click.stop="togglePlay"
        >
          <svg
            v-if="!isPlaying"
            class="video-post-media__play-icon"
            viewBox="0 0 128 128"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M55.0195 39.2262C53.3694 38.2735 51.3363 38.2735 49.6862 39.2263C48.0361 40.179 47.0195 41.9396 47.0195 43.8451V84.1546C47.0195 86.06 48.0361 87.8207 49.6862 88.7734C51.3363 89.7261 53.3694 89.7261 55.0195 88.7734L89.9286 68.6186C91.5788 67.6659 92.5953 65.9052 92.5953 63.9998C92.5953 62.0944 91.5788 60.3337 89.9286 59.381L55.0195 39.2262Z"
              fill="white"
              fill-opacity="0.72"
            />
          </svg>
        </button>

        <section class="video-post-info" @click="onInvokeApp">
          <div class="video-post-author">
            <van-image
              class="video-post-author__avatar"
              fit="cover"
              round
              :src="authorAvatar"
            />
            <span class="video-post-author__name">{{ authorName }}</span>
            <DuTheme name="linjie" class="video-post-author__follow-theme">
              <DuButton
                color="primary"
                type="primary"
                size="small"
                @click.stop="onInvokeApp"
              >
                关注
              </DuButton>
            </DuTheme>
          </div>

          <div v-if="postData.title" class="video-post-topic">
            <p class="video-post-topic__title">{{ postData.title }}</p>
            <button
              type="button"
              class="video-post-topic__follow"
              @click.stop="onInvokeApp"
            >
              跟帖
            </button>
          </div>

          <div v-if="showRelatedMeta" class="video-post-meta">
            <span v-if="activityText" class="video-post-meta__activity">
              {{ activityText }}
            </span>
            <van-image
              v-if="creatorImage"
              class="video-post-meta__image"
              fit="cover"
              round
              :src="creatorImage"
            />
            <span v-if="creatorName" class="video-post-meta__name">
              {{ creatorName }}
            </span>
            <span v-if="creatorName" class="video-post-meta__feature">
              首发
            </span>
            <template v-if="followCountText">
              <span v-if="hasMetaLead" class="video-post-meta__divider">|</span>
              <span class="video-post-meta__count">{{ followCountText }}</span>
            </template>
            <span class="video-post-meta__arrow">›</span>
          </div>

          <p v-if="postSummary" class="video-post-summary">
            <span ref="summaryRef" class="video-post-summary__text">
              {{ postSummary }}
            </span>
            <span v-if="showExpand" class="video-post-summary__expand">
              展开
            </span>
          </p>

          <div v-if="labels.length" class="video-post-labels">
            <div
              v-for="label in labels"
              :key="label.id"
              class="video-post-label"
              @click.stop="onInvokeApp"
            >
              <van-image
                v-if="getLabelImage(label)"
                class="video-post-label__image"
                fit="cover"
                :src="getLabelImage(label)"
              />
              <span class="video-post-label__title">
                {{ getLabelTitle(label) }}
              </span>
            </div>
          </div>

          <div v-if="hotspotName" class="video-post-hotspot">
            <strong>热点</strong>
            <span>{{ hotspotName }}</span>
          </div>

          <time v-if="createdAtText" class="video-post-date">
            {{ createdAtText }}
          </time>
        </section>
      </template>
    </main>

    <footer class="video-post-actions" @click="onInvokeApp">
      <div class="video-post-actions__input">说点什么...</div>
      <button
        type="button"
        class="video-post-actions__open"
        @click.stop="onInvokeApp"
      >
        临界App内打开
      </button>
      <button
        type="button"
        class="video-post-actions__favorite"
        @click.stop="onInvokeApp"
      >
        收藏
      </button>
      <button
        type="button"
        class="video-post-actions__comment"
        @click.stop="onInvokeApp"
      >
        <svg
          class="video-post-actions__comment-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M23 5C23 2.79086 21.2091 1 19 1H5C2.79086 1 1 2.79086 1 5V15C1 17.2091 2.79086 19 5 19H7V21.238C7 23.1087 9.33898 23.9553 10.5364 22.5183L13.4684 19H19C21.2091 19 23 17.2091 23 15V5ZM19 3C20.1046 3 21 3.89543 21 5V15C21 16.1046 20.1046 17 19 17H13.0047C12.7049 17 12.4206 17.1332 12.2288 17.3634L9 21.2379V18.0098C9 17.4212 8.51655 17 7.99 17H5C3.89543 17 3 16.1046 3 15V5C3 3.89543 3.89543 3 5 3H19Z"
            fill="white"
          />
        </svg>
        <span v-if="postData.commentCount > 0">
          {{ postData.commentCount }}
        </span>
      </button>
    </footer>
  </div>
</template>

<script setup>
import {
  computed,
  defineProps,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { Image as VanImage } from "vant";
import { DuButton, DuIcon, DuTheme } from "dangoui";
import dayjs from "dayjs";
import DownloadBanner from "@/components/DownloadBanner/index.vue";
import { invokeAppByYYB } from "@/utils/mob";

const props = defineProps({
  postData: {
    type: Object,
    default: () => ({}),
  },
  detailData: {
    type: Object,
    default: () => ({}),
  },
  postId: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  errorText: {
    type: String,
    default: "",
  },
});

const videoRef = ref(null);
const summaryRef = ref(null);
const isPlaying = ref(false);
const showExpand = ref(false);
let resumeAfterVisible = false;

const postData = computed(() => props.postData || {});
const detailData = computed(() => props.detailData || {});
const firstAttachment = computed(() => postData.value.attachments?.[0] || {});

const videoUrl = computed(() => {
  return (
    firstAttachment.value.videoUrls?.[0]?.url || firstAttachment.value.url || ""
  );
});

const displayError = computed(() => {
  return props.errorText;
});

const isReviewing = computed(() => {
  const status = postData.value.autoReviewStatus;
  return Boolean(status && status !== "processed");
});

const author = computed(
  () => postData.value.user || postData.value.creator || {}
);
const authorAvatar = computed(
  () => author.value.avatar || author.value.avatarUrl || ""
);
const authorName = computed(
  () => author.value.nickName || author.value.displayName || "-"
);

const postSummary = computed(() => {
  return (
    postData.value.summary ||
    postData.value.content ||
    postData.value.rawContent ||
    ""
  );
});

const labels = computed(() => {
  if (!Array.isArray(postData.value.labels)) {
    return [];
  }

  return postData.value.labels
    .filter(
      (item) =>
        !item?.key && item?.relatedType !== "hotspot" && getLabelTitle(item)
    )
    .slice(0, 3);
});

const hotspotLabel = computed(() => {
  if (!Array.isArray(postData.value.labels)) {
    return null;
  }

  return (
    postData.value.labels.find((item) => item?.relatedType === "hotspot") ||
    null
  );
});

const topicInfo = computed(() => detailData.value.topic || {});
const creatorInfo = computed(() => topicInfo.value.creator || {});
const creatorName = computed(
  () =>
    creatorInfo.value.nickName ||
    creatorInfo.value.displayName ||
    creatorInfo.value.name ||
    ""
);
const creatorImage = computed(
  () => creatorInfo.value.avatar || creatorInfo.value.avatarUrl || ""
);

const activityText = computed(() => {
  const { startAt, endAt } = topicInfo.value.event || {};
  if (startAt === undefined || endAt === undefined) {
    return "";
  }

  const now = Date.now();
  const startTime = Number(startAt);
  const endTime = Number(endAt);
  return Number.isFinite(startTime) &&
    Number.isFinite(endTime) &&
    now >= startTime &&
    now <= endTime
    ? "有奖征集中"
    : "";
});

const followCountText = computed(() => {
  const count = postData.value.followInfo?.followCount;
  return count === undefined || count === null || count === ""
    ? ""
    : `${count} 跟帖`;
});

const showRelatedMeta = computed(() => {
  return Boolean(
    activityText.value || creatorName.value || followCountText.value
  );
});

const hasMetaLead = computed(() => {
  return Boolean(activityText.value || creatorName.value);
});

const hotspotName = computed(() => {
  return (
    hotspotLabel.value?.hotspot?.name ||
    hotspotLabel.value?.title ||
    detailData.value.hotspot?.name ||
    detailData.value.hotspot?.title ||
    ""
  );
});

const createdAtText = computed(() => {
  if (!postData.value.createdAt) {
    return "";
  }

  return dayjs(+postData.value.createdAt).format("YYYY-MM-DD");
});

watch(postSummary, () => nextTick(measureSummary));

function onInvokeApp() {
  invokeAppByYYB({
    targetPath: "pages-community/video/list",
    params: {
      postId: props.postId,
    },
  });
}

function togglePlay() {
  if (!videoRef.value) {
    return;
  }

  if (videoRef.value.paused) {
    videoRef.value.play().catch(() => {
      isPlaying.value = false;
    });
    return;
  }

  videoRef.value.pause();
}

function measureSummary() {
  if (!summaryRef.value) {
    showExpand.value = false;
    return;
  }

  showExpand.value =
    summaryRef.value.scrollWidth > summaryRef.value.clientWidth;
}

function handleVisibilityChange() {
  if (!videoRef.value) {
    return;
  }

  if (document.visibilityState === "hidden") {
    resumeAfterVisible = !videoRef.value.paused;
    videoRef.value.pause();
    return;
  }

  if (resumeAfterVisible) {
    videoRef.value.play().catch(() => {
      isPlaying.value = false;
    });
    resumeAfterVisible = false;
  }
}

function getLabelTitle(item) {
  return (
    item?.[item.relatedType]?.name ||
    item?.[item.relatedType]?.categoryName ||
    item?.title ||
    item?.name ||
    ""
  );
}

function getLabelImage(item) {
  const relatedData = item?.[item.relatedType] || {};
  let result =
    relatedData.imgs?.whiteBgPng || relatedData.imgs?.cover || item?.img || "";

  if (item.relatedType === "tag") {
    result = relatedData.tagImages?.logo || result;
  }

  if (item.relatedType === "category") {
    result =
      relatedData.image?.logo || relatedData.mainTag?.tagImages?.logo || result;
  }

  if (item.relatedType === "shelf") {
    result =
      relatedData.images?.coverImage ||
      relatedData.images?.coverImages?.[0] ||
      result;
  }

  if (item.relatedType === "groupon") {
    result = relatedData.coverImages?.[0] || result;
  }

  return result;
}

onMounted(() => {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("resize", measureSummary);
  nextTick(measureSummary);
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("resize", measureSummary);
});
</script>

<style lang="scss" scoped>
.video-post-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: #000;
  color: #fff;
}

.video-post-banner {
  position: relative;
  z-index: 4;
  flex: 0 0 112rpx;
  height: 112rpx;
  overflow: hidden;
  background: #dbd9f8;

  :deep(.banner) {
    width: 100%;
    height: 112rpx;
    object-fit: cover;
  }
}

.video-post-media {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: #000;

  &__video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__play-mask {
    position: absolute;
    z-index: 1;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
  }

  &__play-icon {
    width: 192rpx;
    height: 192rpx;
  }
}

.video-post-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.84);
  font-size: 30rpx;
  font-weight: 500;

  &--reviewing {
    gap: 16rpx;
  }
}

.video-post-info {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: 0;
  left: 0;
  box-sizing: border-box;
  padding: 176rpx 20rpx 16rpx;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.62) 24%,
    #000 63%
  );
}

.video-post-author {
  display: flex;
  align-items: center;
  min-width: 0;

  &__avatar {
    flex: 0 0 64rpx;
    width: 64rpx;
    height: 64rpx;
    overflow: hidden;
    border: 2rpx solid rgba(255, 255, 255, 0.24);
  }

  &__name {
    min-width: 0;
    margin-left: 12rpx;
    overflow: hidden;
    color: #fff;
    font-size: 30rpx;
    font-weight: 500;
    line-height: 44rpx;
    text-overflow: ellipsis;
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.6);
    white-space: nowrap;
  }

  &__follow-theme {
    flex: none;
    margin-left: 16rpx;
  }
}

.video-post-topic {
  display: flex;
  align-items: center;
  margin-top: 14rpx;

  &__title {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #fff;
    font-size: 28rpx;
    font-weight: 600;
    line-height: 42rpx;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__follow {
    flex: none;
    height: 72rpx;
    margin-left: auto;
    padding: 0 24rpx;
    border: 0;
    border-radius: 12rpx;
    background: #181818;
    color: #fff;
    font-size: 28rpx;
    font-weight: 500;
  }
}

.video-post-meta {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 36rpx;
  margin-top: 4rpx;
  color: rgba(255, 255, 255, 0.64);
  font-size: 22rpx;
  line-height: 32rpx;

  &__activity {
    flex: none;
    margin-right: 8rpx;
    padding: 0 8rpx;
    border-radius: 6rpx;
    background: #df3d4b;
    color: #fff;
  }

  &__image {
    flex: 0 0 32rpx;
    width: 32rpx;
    height: 32rpx;
    margin-right: 6rpx;
    overflow: hidden;
  }

  &__name {
    max-width: 180rpx;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.78);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__feature {
    margin-left: 8rpx;
  }

  &__divider {
    margin: 0 10rpx;
  }

  &__arrow {
    margin-left: 8rpx;
    font-size: 32rpx;
  }
}

.video-post-summary {
  display: flex;
  margin: 14rpx 0 0;
  overflow: hidden;
  color: #fff;
  font-size: 26rpx;
  line-height: 40rpx;

  &__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__expand {
    flex: none;
    padding-left: 10rpx;
    color: rgba(255, 255, 255, 0.56);
  }
}

.video-post-labels {
  display: flex;
  gap: 16rpx;
  margin-top: 14rpx;
  overflow-x: auto;
  color: #fff;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.video-post-label {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8rpx;
  box-sizing: border-box;
  height: 52rpx;
  padding: 0 12rpx;
  border-radius: 10rpx;
  background: #181818;

  &__image {
    width: 32rpx;
    height: 32rpx;
    overflow: hidden;
    border-radius: 6rpx;
    background: #fff;
  }

  &__title {
    max-width: 220rpx;
    overflow: hidden;
    font-size: 24rpx;
    font-weight: 500;
    line-height: 36rpx;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.video-post-hotspot {
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  height: 52rpx;
  box-sizing: border-box;
  margin-top: 14rpx;
  padding: 0 12rpx;
  border-radius: 10rpx;
  background: #181818;
  color: #fff;
  font-size: 24rpx;
  line-height: 36rpx;

  strong {
    margin-right: 10rpx;
    font-weight: 600;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.video-post-date {
  display: block;
  margin-top: 14rpx;
  color: rgba(255, 255, 255, 0.36);
  font-size: 22rpx;
  line-height: 32rpx;
}

.video-post-actions {
  position: relative;
  display: grid;
  flex: 0 0 108rpx;
  grid-template-columns: 178rpx 354rpx 88rpx 1fr;
  align-items: center;
  box-sizing: content-box;
  height: 108rpx;
  padding: 0 20rpx max(env(safe-area-inset-bottom), 32rpx);
  border-top: 2rpx solid rgba(255, 255, 255, 0.72);
  background: #000;
  color: #fff;

  button {
    padding: 0;
    border: 0;
    color: inherit;
    font-family: inherit;
  }

  &__input {
    z-index: 0;
    display: flex;
    align-items: center;
    align-self: center;
    box-sizing: border-box;
    width: 240rpx;
    height: 76rpx;
    padding: 0 18rpx;
    overflow: hidden;
    border-radius: 16rpx;
    background: #151515;
    color: rgba(255, 255, 255, 0.42);
    font-size: 24rpx;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__open {
    z-index: 1;
    width: 354rpx;
    height: 76rpx;
    border-radius: 16rpx;
    background: linear-gradient(100deg, #3426d6 0%, #7937dc 100%);
    font-size: 30rpx;
    font-weight: 500;
  }

  &__favorite {
    height: 76rpx;
    background: transparent;
    font-size: 26rpx;
  }

  &__comment {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8rpx;
    height: 76rpx;
    background: transparent;
    font-size: 24rpx;
  }

  &__comment-icon {
    flex: 0 0 48rpx;
    width: 48rpx;
    height: 48rpx;
  }
}
</style>
